import React, { useEffect } from 'react';
import { Button, CircularProgress, Grid, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography } from '@mui/material';
import { EmployerCompany } from '../../../@types';
import { useNotificationStatistics } from '../../../hooks/useNotificationStatistics';
import { useSnackBar } from '../../../hooks';

export type NotificationPropsType = {
    taxNumber: string;
    date: string;
};

const NotificationStatistics = ({ taxNumber, date }: NotificationPropsType) => {
    const today = new Date();

    const {
        loading,
        statisticsList,
        consecutiveDays,
        originalWorkdaysAtOtherCompany,
        workdaysAtOtherCompany,
        setWorkdaysAtOtherCompany,
        updateWorkdaysAtOtherCompany,
        error,
    } = useNotificationStatistics(taxNumber, date);

    const sumWorkDays = statisticsList.reduce((sum, companyStats) => sum + (companyStats.yearly || 0), originalWorkdaysAtOtherCompany ?? 0);
    const { showError, snackBar } = useSnackBar();

    useEffect(() => {
        if (error) {
            showError(<Typography>{error}</Typography>);
        }
    }, [error]);

    return (
        <>
            {snackBar()}
            <Grid container p={0} pt={2} justifyContent={'center'}>
                {loading ? (
                    <CircularProgress />
                ) : (
                    <TableContainer component={Paper}>
                        <Table sx={{ minWidth: '100%', minHeight: 200 }} size='small' aria-label='notification statistics table'>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Cég</TableCell>
                                    <TableCell align='center'>{today.getFullYear()}</TableCell>
                                    <TableCell align='center'>
                                        {today.toLocaleString('hu-HU', {
                                            month: 'long',
                                        })}
                                    </TableCell>
                                    <TableCell align='center'>egybefüggő</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {Object.keys(EmployerCompany).map(company => {
                                    const companyStatistics = statisticsList.find(s => s.company === company);

                                    return (
                                        <TableRow key={company}>
                                            <TableCell>{EmployerCompany[company as keyof typeof EmployerCompany]}</TableCell>
                                            <TableCell align='center'>{companyStatistics?.yearly ?? 0}</TableCell>
                                            <TableCell align='center'>15/{companyStatistics?.monthly ?? 0}</TableCell>
                                            <TableCell align='center'>5/{consecutiveDays?.company === company ? consecutiveDays.count : 0}</TableCell>
                                        </TableRow>
                                    );
                                })}

                                <TableRow key='OtherCompanyWorkDays'>
                                    <TableCell>Más cégnél töltött napok</TableCell>
                                    <TableCell align='center'>
                                        <TextField
                                            size='small'
                                            value={workdaysAtOtherCompany ?? 0}
                                            onChange={e => setWorkdaysAtOtherCompany(Number(e.target.value))}
                                            inputProps={{
                                                inputMode: 'numeric',
                                                pattern: '[0-9]*',
                                                style: { textAlign: 'center', padding: '4px', maxWidth: '60px' },
                                            }}
                                            sx={{ width: '70px' }}
                                        />
                                    </TableCell>
                                    <TableCell></TableCell>
                                    <TableCell align='center'>
                                        <Button
                                            sx={{
                                                mx: 1,
                                                borderRadius: 50,
                                                maxHeight: '30px',
                                                maxWidth: '50px',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                            }}
                                            variant={'contained'}
                                            color={'warning'}
                                            disabled={workdaysAtOtherCompany === originalWorkdaysAtOtherCompany}
                                            onClick={updateWorkdaysAtOtherCompany}>
                                            Mentés
                                        </Button>
                                    </TableCell>
                                </TableRow>

                                <TableRow>
                                    <TableCell>Összes bejelentett nap az évben</TableCell>
                                    <TableCell align='center'>120/{sumWorkDays}</TableCell>
                                </TableRow>
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </Grid>
        </>
    );
};

export { NotificationStatistics };
